/**
 * Content Service - Fetches content from unified backend API
 * This service connects to the Mango Business Systems backend
 */

import { CacheManager } from '../utils/cacheManager';
import safeStorage from '../utils/safeStorage';
import { STORAGE_KEYS } from '../utils/storageKeys';
import { API_BASE_URL, BUSINESS_ID } from '../config/api';

class ContentService {
  constructor() {
    this.baseUrl = window.location.origin;
    this.apiBaseUrl = API_BASE_URL;
    this.businessId = BUSINESS_ID;
    this.cacheManager = new CacheManager(5 * 60 * 1000); // 5 minutes
    this.businessIdResolved = false;
    this.businessIdResolvePromise = null;
    this.requestCounter = 0;
  }

  pushDebug(label, data = {}) {
    const entry = {
      at: new Date().toISOString(),
      label,
      data,
    };
    if (typeof window !== 'undefined') {
      const prev = Array.isArray(window.__CONTENT_DEBUG) ? window.__CONTENT_DEBUG : [];
      window.__CONTENT_DEBUG = [...prev.slice(-39), entry];
    }
    console.log(`[CONTENT-DEBUG] ${label}`, data);
  }

  getApiBaseCandidates() {
    const candidates = [this.apiBaseUrl, 'https://api.mango-business.solutions/api/v1'];
    return [...new Set(candidates.filter(Boolean))];
  }

  async fetchJsonWithFallback(path) {
    const candidates = this.getApiBaseCandidates();
    let lastError = null;
    for (const base of candidates) {
      const url = `${base}${path}`;
      this.pushDebug('fetchJsonWithFallback.try', { url });
      try {
        const response = await fetch(url);
        if (!response.ok) {
          this.pushDebug('fetchJsonWithFallback.non_ok', { url, status: response.status });
          lastError = new Error(`HTTP ${response.status} from ${url}`);
          continue;
        }
        const json = await response.json();
        this.pushDebug('fetchJsonWithFallback.ok', {
          url,
          status: response.status,
          contentCount: Array.isArray(json?.content) ? json.content.length : undefined,
        });
        return json;
      } catch (error) {
        this.pushDebug('fetchJsonWithFallback.error', { url, error: String(error) });
        lastError = error;
      }
    }
    throw lastError || new Error(`Failed to fetch ${path}`);
  }

  async fetchAuthenticatedContentWithFallback() {
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    const candidates = this.getApiBaseCandidates();
    for (const base of candidates) {
      try {
        const response = await fetch(`${base}/content`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) continue;
        const data = await response.json();
        if (Array.isArray(data?.content)) return data;
      } catch {
        // Try next candidate.
      }
    }
    return null;
  }

  async resolveBusinessIdByDomain() {
    if (typeof window === 'undefined') return;
    if (this.businessIdResolved) return;
    if (this.businessIdResolvePromise) {
      await this.businessIdResolvePromise;
      return;
    }

    this.businessIdResolvePromise = (async () => {
      const domain = window.location.hostname;
      const candidates = this.getApiBaseCandidates();
      for (const base of candidates) {
        try {
          const response = await fetch(`${base}/business/by-domain?domain=${encodeURIComponent(domain)}`);
          if (!response.ok) continue;
          const data = await response.json();
          const resolvedId = data?.business?.id;
          if (resolvedId && typeof resolvedId === 'string' && resolvedId !== this.businessId) {
            console.log(`🔁 [CONTENT-SERVICE] Resolved businessId by domain (${domain}):`, resolvedId);
            this.businessId = resolvedId;
          }
          return;
        } catch {
          // Try next API candidate.
        }
      }
    })();

    try {
      await this.businessIdResolvePromise;
    } finally {
      this.businessIdResolved = true;
      this.businessIdResolvePromise = null;
    }
  }

  /**
   * Transform backend content format to frontend format
   */
  transformContentItem(item) {
    // Parse images and videos arrays (they're stored as JSON in database)
    let images = [];
    let videos = [];
    
    if (item.images) {
      try {
        images = typeof item.images === 'string' ? JSON.parse(item.images) : item.images;
        if (!Array.isArray(images)) images = [];
      } catch (e) {
        console.warn('Failed to parse images array:', e);
        images = [];
      }
    }
    
    if (item.videos) {
      try {
        videos = typeof item.videos === 'string' ? JSON.parse(item.videos) : item.videos;
        if (!Array.isArray(videos)) videos = [];
      } catch (e) {
        console.warn('Failed to parse videos array:', e);
        videos = [];
      }
    }
    
    // Add coverImage to images if not already included
    if (item.coverImage && !images.includes(item.coverImage)) {
      images.unshift(item.coverImage);
    }
    
    // If no images but has coverImage, use coverImage
    if (images.length === 0 && item.coverImage) {
      images = [item.coverImage];
    }

    // Parse tags if it's a string (JSON field from database)
    let tags = null;
    if (item.tags) {
      try {
        tags = typeof item.tags === 'string' ? JSON.parse(item.tags) : item.tags;
        // Resolve audioUrl if it exists in tags
        if (tags && typeof tags === 'object' && tags.audioUrl) {
          tags.audioUrl = this.getFullMediaUrl(tags.audioUrl);
        }
      } catch (e) {
        console.warn('Failed to parse tags:', e);
        tags = null;
      }
    }

    return {
      id: item.id,
      name: item.title || item.name || 'Untitled', // Map title to name for frontend
      title: item.title,
      description: item.description || '',
      content: item.content || item.description || '',
      coverImage: item.coverImage ? this.getFullMediaUrl(item.coverImage) : null,
      images: images.map(url => this.getFullMediaUrl(url)).filter(Boolean),
      videos: videos.map(url => this.getFullMediaUrl(url)).filter(Boolean),
      tags: tags, // Include tags for music player to access audioUrl (with resolved URLs)
      postedAt: item.publishDate || item.createdAt || item.updatedAt || null,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      contentType: item.contentType || 'blog',
      contentSubcategory: item.contentSubcategory || null, // Include subcategory for music detection
      folder: item.id, // Use ID as folder for compatibility
    };
  }

  /**
   * Get full media URL (handles both relative and absolute URLs)
   */
  getFullMediaUrl(url) {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If it's a relative path, prepend the backend base URL
    if (url.startsWith('/uploads/')) {
      // Backend uploads - construct full URL
      // API_BASE_URL is like: https://api.rainbow-sails.shop/api/v1
      // We need: https://api.rainbow-sails.shop/uploads/...
      const backendBaseUrl = this.apiBaseUrl.replace('/api/v1', '');
      return `${backendBaseUrl}${url}`;
    }
    // If it's already a full path from origin, use it
    if (url.startsWith('/')) {
      return `${this.baseUrl}${url}`;
    }
    return url;
  }

  /**
   * Get all content from the unified backend API
   * @returns {Promise<Array>} Array of content objects
   */
  async getContent() {
    // Check cache first
    const cachedContent = this.cacheManager.get();
    if (cachedContent) {
      return cachedContent;
    }

    try {
      const requestId = ++this.requestCounter;
      this.pushDebug('getContent.start', {
        requestId,
        businessIdBeforeResolve: this.businessId,
        hostname: typeof window !== 'undefined' ? window.location.hostname : 'n/a',
      });
      await this.resolveBusinessIdByDomain();
      this.pushDebug('getContent.after_resolve', { requestId, businessId: this.businessId });
      console.log('🌐 Fetching content from unified backend API...');
      console.log('📍 API URL candidates:', this.getApiBaseCandidates().map((base) => `${base}/content/public?businessId=${this.businessId}`));

      let data = await this.fetchJsonWithFallback(`/content/public?businessId=${this.businessId}`);
      if (!Array.isArray(data?.content) || data.content.length === 0) {
        const authData = await this.fetchAuthenticatedContentWithFallback();
        if (authData?.content?.length) {
          console.log('🔁 Public content empty; using authenticated content API fallback.');
          data = authData;
        }
      }
      console.log('✅ Content loaded from backend API:', data);
      
      // Transform backend format to frontend format
      const content = (data.content || []).map(item => this.transformContentItem(item));
      
      console.log('📊 Content data processed:', {
        contentCount: content.length,
        content: content.map(c => ({ 
          id: c.id, 
          name: c.name, 
          title: c.title,
          contentType: c.contentType,
          hasTags: !!c.tags,
          hasAudioUrl: !!(c.tags && c.tags.audioUrl)
        }))
      });
      
      // Cache the results
      this.cacheManager.set(content);
      this.pushDebug('getContent.success', {
        requestId,
        businessId: this.businessId,
        contentCount: content.length,
        firstTitle: content[0]?.title || content[0]?.name || null,
      });
      
      // Also save to localStorage as fallback
      this.saveContentToLocalStorage(content);
      
      return content;
    } catch (error) {
      this.pushDebug('getContent.error', { error: String(error), businessId: this.businessId });
      console.error('❌ Error fetching content from API:', error);
      
      // Fallback to localStorage if API fetch fails (only for offline scenarios)
      const fallbackContent = this.getContentFromLocalStorage();
      if (fallbackContent.length > 0) {
        console.warn('⚠️ API fetch failed, using localStorage fallback. This may be stale data.');
        console.log('📦 [CONTENT-SERVICE] Fallback content from localStorage:', {
          count: fallbackContent.length,
          items: fallbackContent.map(c => ({
            id: c.id,
            title: c.title || c.name,
            contentType: c.contentType,
            hasTags: !!c.tags,
            tagsType: typeof c.tags,
            tagsValue: c.tags,
            hasAudioUrl: !!(c.tags && c.tags.audioUrl)
          }))
        });
        return fallbackContent;
      }
      
      // No fallback to static files - we only use the unified backend API
      console.error('❌ Failed to fetch content from backend API and no localStorage cache available');
      return [];
    }
  }

  async getMusicPlayerEnabled() {
    try {
      await this.resolveBusinessIdByDomain();
      const data = await this.fetchJsonWithFallback(`/content/music-player/enabled?businessId=${this.businessId}`);
      return data?.enabled !== false;
    } catch (error) {
      console.warn('🎵 Failed to check player enabled status:', error);
      return true;
    }
  }

  /**
   * Get a specific content item by ID from the unified backend API
   * @param {string} contentId - The content ID
   * @returns {Promise<Object|null>} Content data or null if not found
   */
  async getContentItem(contentId) {
    try {
      await this.resolveBusinessIdByDomain();
      console.log(`🔍 Fetching content data for: ${contentId}`);
      
      const response = await fetch(`${this.apiBaseUrl}/content/public/${contentId}?businessId=${this.businessId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Content data loaded:', data);
      
      if (data.content) {
        return this.transformContentItem(data.content);
      }
      
      return null;
    } catch (error) {
      console.error(`❌ Error fetching content ${contentId}:`, error);
      
      // No fallback to static files - we only use the unified backend API
      console.error(`❌ Failed to fetch content ${contentId} from backend API`);
      return null;
    }
  }

  /**
   * Get content from localStorage (fallback)
   */
  getContentFromLocalStorage() {
    const content = safeStorage.getJSON(STORAGE_KEYS.CONTENT || 'rainbow_sails_content', []);
    console.log('💾 [CONTENT-SERVICE] Retrieved from localStorage:', {
      count: content.length,
      sample: content.length > 0 ? {
        id: content[0].id,
        title: content[0].title || content[0].name,
        contentType: content[0].contentType,
        hasTags: !!content[0].tags,
        tagsType: typeof content[0].tags,
        tagsValue: content[0].tags
      } : null
    });
    return content;
  }

  /**
   * Save content to localStorage (for offline access)
   */
  saveContentToLocalStorage(content) {
    if (safeStorage.setJSON(STORAGE_KEYS.CONTENT || 'rainbow_sails_content', content)) {
      const musicContent = content.filter(c => 
        c.contentType === 'music' || (c.tags && c.tags.audioUrl)
      );
      console.log('💾 Content saved to localStorage:', {
        totalItems: content.length,
        musicItems: musicContent.length,
        musicItemsWithAudio: musicContent.filter(c => c.tags && c.tags.audioUrl).length
      });
    }
  }

  /**
   * Clear cache (useful for testing or when you know data has changed)
   */
  clearCache() {
    this.cacheManager.clear();
    console.log('🗑️ Content cache cleared');
  }
}

// Export a singleton instance
const contentServiceInstance = new ContentService();
export default contentServiceInstance;

